/**
 * The room's library of NAMED effect looks — what a save captures and what a recall hands back.
 *
 * It lives in its own module rather than beside the catalog because a look is downstream of BOTH of the
 * things it is made of: an effect package's parameter vocabulary (`effectPackages`) and, for the one
 * effect that has one, the audio-reactive layer stack (`effectStack`). `effectStack` already depends on
 * `effectPackages`, so reading a preset from `effectPackages` would have made the two import each other.
 * A preset is the third thing, and it sits on top of both.
 */

import type { EffectPackageParameterValue } from './effectPackages';
import { parseEffectStack, type EffectStack } from './effectStack';

/**
 * One named look an operator saved for an effect in this room.
 *
 * The room's REMEMBERED values are its working copy — recoverable by re-tuning, and so safe to keep on
 * the Pi's own disk. A named preset is authored work that is not recoverable, so it lives with the
 * venue: `venues/{venueId}/rooms/{roomId}/effectPresets`, written only by the cloud's own callables
 * and read here through a live listener. A look named "Sunday slow" belongs to the room, not to the SD
 * card currently in it.
 *
 * `stack` is the audio-reactive half: a baseline and the layers the music pushes on top of it.
 */
export interface EffectPreset {
  presetId: string;
  packageId: string;
  name: string;
  values: Record<string, EffectPackageParameterValue>;
  stack?: EffectStack;
}

function plainObject(value: unknown): Record<string, unknown> | undefined {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : undefined;
}

function optionalString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

function parsePresetValues(value: unknown): Record<string, EffectPackageParameterValue> {
  const values: Record<string, EffectPackageParameterValue> = {};
  for (const [key, entry] of Object.entries(plainObject(value) ?? {})) {
    if (typeof entry === 'number' && Number.isFinite(entry)) values[key] = entry;
    else if (typeof entry === 'boolean' || typeof entry === 'string') values[key] = entry;
  }
  return values;
}

/**
 * One preset, from whatever the cloud handed over — a listener document or a callable's answer.
 *
 * Every field is checked rather than trusted. These documents are written by a callable today, but a
 * malformed one must cost the operator one missing row, never a card that fails to render — and a
 * recall whose stack the console cannot read must cost that recall its stack, never the whole gesture.
 */
export function parseEffectPreset(document: unknown): EffectPreset | undefined {
  const raw = plainObject(document);
  if (!raw) return undefined;
  const presetId = optionalString(raw.presetId);
  const packageId = optionalString(raw.packageId);
  const name = optionalString(raw.name);
  if (!presetId || !packageId || !name) return undefined;
  const stack = raw.stack == null ? undefined : parseEffectStack(raw.stack);
  return {
    presetId,
    packageId,
    name,
    values: parsePresetValues(raw.values),
    ...(stack ? { stack } : {}),
  };
}

/**
 * The room's preset library, from whatever the listener handed over.
 *
 * Presets sort by name because that is the only ordering an operator can predict — and because a rename
 * that reshuffled the list under the hand that renamed it would be its own small bug.
 */
export function parseEffectPresets(documents: readonly unknown[]): EffectPreset[] {
  const presets: EffectPreset[] = [];
  for (const document of documents) {
    const preset = parseEffectPreset(document);
    if (preset) presets.push(preset);
  }
  return presets.sort((left, right) => left.name.localeCompare(right.name));
}
